import * as React from "react";
import { select } from "d3-selection";
import { mapStatusToStyle } from "./DashboardUtils";
import { hashCode } from "../../lib/Util";
import type { DashboardData, DashboardResultIndicatorObjLine, DashboardLayout } from "../../lib/Models";
import type { Selection } from "d3-selection";

type SimpleKpi = {
    status: string;
    title: string;
    url: string;
    x: number;
    y: number;
};

type ManLineKpi = {
    person: string;
    product: string;
    status: string;
    title: string;
    url: string;
    workorder_is_late: Boolean;
    x: number;
    y: number;
};

type State = {
    width: number;
    height: number;
    svg: Selection<SVGSVGElement>;
};

type Props = {
    background_url: string;
    kpis: SimpleKpi[];
    title: string;
    manLineKpis: ManLineKpi[];
};

const TOTAL_WIDTH = 1000;
const TOTAL_HEIGHT = 500;
const TITLE_WIDTH = 200;
const FONT_SIZE = 9;
const FONT_SIZE2 = 9;
const FONT_SIZE3 = 7;
const PADDING = 4;
const PADDING2 = 4;
const ROUND_CORNER = 2;
const ICON_COLOR = "#666666"; // "#CBD2D9";
const TEXT_COLOR = "#222222";
const KPI_WIDTH = 130;
const KPI_WIDTH_SMALL = 120;

class OverlayDash extends React.Component<Props, State> {
    nodeRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        window.addEventListener("resize", () => this.updateDimensions());
        this.updateDimensions();
    }

    updateDimensions() {
        const width = window.innerWidth - 20;
        const height = width / 2;
        const node = this.nodeRef.current; // TOTEST
        this.setState({ width, height });
        select(node).selectAll("*").remove();
        const svg: Selection<SVGSVGElement> = select(node)
            .append("svg")
            .attr("width", width)
            .attr("height", height)
            .attr("viewBox", `0 0 ${TOTAL_WIDTH} ${TOTAL_HEIGHT}`);
        this.setState({ svg }, () => this.createDisplay());
    }

    createSimpleKpis(svg: Selection<SVGSVGElement>) {
        for (const kpi of this.props.kpis) {
            const g = svg.append("g").attr("transform", function () {
                return "translate(" + kpi.x + "," + kpi.y + ")";
            });
            const width = KPI_WIDTH_SMALL;
            const rect: Selection<SVGRectElement> = g
                .append("rect")
                .attr("x", 0)
                .attr("y", 0)
                .attr("rx", 4)
                .attr("rx", 4)
                .attr("fill-opacity", "0.8")
                .attr("class", mapStatusToStyle(kpi.status))
                .attr("width", width)
                .attr("height", 2 * PADDING + FONT_SIZE);
            const text = g
                .append("text")
                .attr("x", width / 2)
                .attr("y", PADDING + FONT_SIZE / 2)
                .attr("font-family", "sans-serif")
                .attr("font-size", FONT_SIZE + "px")
                .attr("fill", "white")
                .attr("dominant-baseline", "central")
                .attr("text-anchor", "middle")
                .text(kpi.title);
            if (kpi.url) {
                rect.on("click", () => {
                    window.location.href = kpi.url;
                });
                rect.style("cursor", "pointer");
                text.on("click", () => {
                    window.location.href = kpi.url;
                });
                text.style("cursor", "pointer");
            }
        }
    }

    createManLineKpis(svg: Selection<SVGSVGElement>) {
        for (const kpi of this.props.manLineKpis) {
            const g: Selection<SVGGElement> = svg.append("g").attr("transform", function () {
                return "translate(" + kpi.x + "," + kpi.y + ")";
            });
            const width = KPI_WIDTH;

            const header_height = 2 * PADDING + FONT_SIZE;
            const total_height =
                header_height + 1 * PADDING + 1 * (PADDING2 + FONT_SIZE2) + 3 * (PADDING2 + FONT_SIZE3);
            const ctrls: Selection<SVGElement>[] = [];
            const rect: Selection<SVGRectElement> = g
                .append("rect")
                .attr("x", 0)
                .attr("y", 0)
                .attr("rx", ROUND_CORNER)
                .attr("rx", ROUND_CORNER)
                .attr("fill", "#ffffff")
                .attr("fill-opacity", "0.7")
                .attr("width", width)
                .attr("height", total_height);
            ctrls.push(rect);

            // create header

            const recx: Selection<SVGRectElement> = g
                .append("rect")
                .attr("x", 0)
                .attr("y", 0)
                .attr("rx", 4)
                .attr("rx", 4)
                .attr(
                    "class",
                    mapStatusToStyle(kpi.status === "on" ? "ok" : kpi.status === "downtime" ? "error" : "inactive")
                )
                .attr("width", width)
                .attr("height", header_height);
            ctrls.push(recx);
            const text: Selection<SVGTextElement> = g
                .append("text")
                .attr("x", width / 2)
                .attr("y", PADDING + FONT_SIZE / 2)
                .attr("font-size", FONT_SIZE + "px")
                .attr("fill", "white")
                .attr("dominant-baseline", "central")
                .attr("text-anchor", "middle")
                .text(kpi.title);
            ctrls.push(text);

            // create texts
            let line_index = 0;
            const y_2 = header_height + (line_index + 1) * PADDING2 + line_index * FONT_SIZE2;
            const led2: Selection<SVGCircleElement> = g
                .append("circle")
                .attr("r", 4)
                .attr("cx", PADDING + 2 * FONT_SIZE2 + 2)
                .attr("cy", y_2 + FONT_SIZE2 / 2)
                .attr("class", mapStatusToStyle(kpi.workorder_is_late ? "error" : "ok"));
            ctrls.push(led2);

            line_index++;
            let line_index2 = 0;
            const y_5 =
                header_height + (line_index + 1) * PADDING2 + line_index * FONT_SIZE2 + line_index2 * FONT_SIZE3;
            const text5: Selection<SVGTextElement> = g
                .append("text")
                .attr("x", width / 2)
                .attr("y", y_5 + FONT_SIZE3)
                .attr("font-size", FONT_SIZE3 + "px")
                .attr("fill", TEXT_COLOR)
                .attr("text-anchor", "middle")
                .text(kpi.product);
            ctrls.push(text5);

            line_index2++;
            const y_6 =
                header_height + (line_index + 1) * PADDING2 + line_index * FONT_SIZE2 + line_index2 * FONT_SIZE3;
            const text6: Selection<SVGTextElement> = g
                .append("text")
                .attr("x", width / 2)
                .attr("y", y_6 + FONT_SIZE3)
                .attr("font-size", FONT_SIZE3 + "px")
                .attr("fill", TEXT_COLOR)
                .attr("text-anchor", "middle")
                .text(kpi.person);
            ctrls.push(text6);

            // append icons

            const icon_parts: Selection<SVGGElement> = g
                .append("g")
                .attr("transform", `translate(${PADDING},${y_2 - FONT_SIZE2 / 2}) scale(0.75)`);
            icon_parts
                .append("path")
                .attr("fill", ICON_COLOR)
                .attr(
                    "d",
                    "M4.9,7.6l6.8-3c0.2-0.1,0.4-0.1,0.6,0l6.8,3c0.4,0.2,0.5,0.6,0.4,1c-0.1,0.2-0.2,0.3-0.4,0.4l-6.8,3 c-0.2,0.1-0.4,0.1-0.6,0l-6.8-3c-0.4-0.2-0.5-0.6-0.4-1C4.7,7.8,4.8,7.6,4.9,7.6z"
                );
            icon_parts
                .append("path")
                .attr("fill", ICON_COLOR)
                .attr(
                    "d",
                    "M5.9,10.9l5.8,2.6c0.2,0.1,0.4,0.1,0.6,0l5.8-2.6l1,0.4c0.4,0.2,0.5,0.6,0.4,1c-0.1,0.2-0.2,0.3-0.4,0.4l-6.8,3 c-0.2,0.1-0.4,0.1-0.6,0l-6.8-3c-0.4-0.2-0.5-0.6-0.4-1c0.1-0.2,0.2-0.3,0.4-0.4C4.9,11.3,5.9,10.9,5.9,10.9z M5.9,14.6l5.8,2.6 c0.2,0.1,0.4,0.1,0.6,0l5.8-2.6l1,0.4c0.4,0.2,0.5,0.6,0.4,1c-0.1,0.2-0.2,0.3-0.4,0.4l-6.8,3c-0.2,0.1-0.4,0.1-0.6,0l-6.8-3 c-0.4-0.2-0.5-0.6-0.4-1c0.1-0.2,0.2-0.3,0.4-0.4C4.9,15.1,5.9,14.6,5.9,14.6z"
                );

            if (kpi.url) {
                // make svg elements clickable
                ctrls.forEach(ctrl => {
                    ctrl.on("click", () => {
                        window.location.href = kpi.url;
                    });
                    ctrl.style("cursor", "pointer");
                });
            }
        }
    }

    createDisplay() {
        if (this.state) {
            const svg = this.state.svg;
            const url = this.props.background_url || "http://lorempixel.com/500/250/";
            svg.append("image").attr("xlink:href", url).attr("width", 1000).attr("height", 500);

            const g: Selection<SVGGElement> = svg
                .append("g")
                .attr("transform", `translate(${TOTAL_WIDTH - TITLE_WIDTH - PADDING2}, ${PADDING2})`);
            g.append("rect")
                .attr("x", 0)
                .attr("y", ROUND_CORNER)
                .attr("rx", ROUND_CORNER)
                .attr("rx", ROUND_CORNER)
                .attr("fill", "#1C2837")
                .attr("width", TITLE_WIDTH)
                .attr("height", FONT_SIZE2 + 2 * PADDING2);
            g.append("text")
                .attr("x", PADDING2)
                .attr("y", PADDING2 + FONT_SIZE2)
                .attr("font-size", FONT_SIZE2 + "px")
                .attr("fill", "#ffffff")
                .attr("text-anchor", "start")
                .text(this.props.title);

            this.createSimpleKpis(svg);
            this.createManLineKpis(svg);
        }
    }

    render() {
        return <div className="chart-wrapper" ref={this.nodeRef}></div>;
    }
}

export function createOverlayDashboard(root: DashboardLayout, data: DashboardData) {
    const background_url = root.background || "/img/tmp/kfh_floor_flat.svg";
    const hashKey = hashCode(JSON.stringify(root || ""));
    root.indicators = root.indicators || [];
    data.indicators = data.indicators || [];
    const joined = root.indicators
        .map(x => {
            const hits = data.indicators.filter(y => x.key === y.key);
            if (hits.length === 0) {
                return null;
            }
            return { layout: x, data: hits[0] };
        })
        .filter(x => !!x) as {
        layout: DashboardResultIndicatorObjLine;
        data: DashboardResultIndicatorObjLine;
    }[];
    const kpis = joined
        .filter(x => x.data.type === "ts")
        .map(x => ({
            x: x.layout.x,
            y: x.layout.y,
            title: x.layout.title,
            status: x.data.status,
            value: +x.data.value,
            url: x.layout.url,
        }));
    const manLineKpis: ManLineKpi[] = joined
        .filter(x => x.data.type === "line")
        .map(x => {
            const res: ManLineKpi = {
                person: x.data.person,
                product: x.data.product,
                status: x.data.status,
                title: x.data.title,
                url: x.layout.url,
                workorder_is_late: x.data.workorder_is_late,
                x: x.layout.x,
                y: x.layout.y,
            };
            return res;
        });

    return (
        // class "ql-dash-finalized" is used to determine when the dashboard rendering has been finalized
        // this is important for png and pdf rendering of reports
        <div className={"full-height ql-dash-finalized"}>
            <OverlayDash
                key={"overlay_" + hashKey}
                background_url={background_url}
                kpis={kpis}
                manLineKpis={manLineKpis}
                title={root.title}
            ></OverlayDash>
        </div>
    );
}
